/*
 * Where a company sits in the tree: HQ (The Trades), a brand right below HQ, or a location (or franchise) below a brand.
 * The tree has no stored level; it comes from the trade ("hq") and the companies above, so moving a company never leaves a
 * stale label behind.
 */
import { z } from "zod";

export const COMPANY_LEVELS = ["hq", "brand", "location"] as const;
export type CompanyLevel = (typeof COMPANY_LEVELS)[number];
export const companyLevelSchema = z.enum(COMPANY_LEVELS);

/**
 * The level of the last company in a chain (from the top down to the company itself). A company with nothing above it, or
 * with HQ right above it, is a brand. Anything lower is a location or franchise.
 */
export function companyLevel(chain: readonly { trade: string }[]): CompanyLevel {
  const self = chain.at(-1);
  if (!self || self.trade === "hq") return "hq";
  const parent = chain.at(-2);
  return !parent || parent.trade === "hq" ? "brand" : "location";
}

/**
 * Business info a location (or franchise) can't change itself: its name, phone and email are set by the brand or HQ, so every
 * location reads the same to clients. Only an owner of a company above it can change them.
 */
export const LOCATION_LOCKED_FIELDS = ["name", "phone", "email"] as const;
export type LocationLockedField = (typeof LOCATION_LOCKED_FIELDS)[number];
